import { createFileRoute, Outlet } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TabsBar } from '@/common/TabsBar';
import { FollowUpsAlertContext } from '@/follow-up/FollowUpsAlertContext';
import { useGroupFollowUpsTabAccess } from '~/hooks/useGroupFollowUpsTabAccess';
import { formatString } from '~/utils/stringFormat';

type FollowUpTab = { title: string; to: string };

/**
 * The three subnavs, in legacy's order. `entries` is the one legacy titles
 * "Follow-ups".
 */
function useFollowUpTabs(): FollowUpTab[] {
  const { t } = useTranslation();

  return [
    { title: t('wbs.follow-up-requests'), to: 'follow-up-requests' },
    { title: t('wbs.pending-requests'), to: 'pending-requests' },
    { title: t('sidebar.follow-ups'), to: 'entries' },
  ];
}

function FollowUpsLayout(): ReactElement | null {
  const tabs = useFollowUpTabs();
  const { t } = useTranslation();
  const { canView, isResolved } = useGroupFollowUpsTabAccess();
  const [duplicateBlocked, setDuplicateBlocked] = useState(false);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [canceledRequestId, setCanceledRequestId] = useState<string | null>(
    null,
  );

  // Not a no-op: painting the tab strip before the group type and the viewer's
  // profile land would flash it at a viewer the next line refuses.
  if (!isResolved) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <FollowUpsAlertContext.Provider
      value={{
        setDuplicateBlocked,
        setCreatedRequestId,
        setCanceledRequestId,
      }}
    >
      {duplicateBlocked && (
        <AlertBox variant="error">{t('wbs.this-student-has-already')}</AlertBox>
      )}
      {createdRequestId && (
        <AlertBox variant="success">
          {formatString(t('wbs.follow-up-request-s-created'), createdRequestId)}
        </AlertBox>
      )}
      {canceledRequestId && (
        <AlertBox variant="success">
          {formatString(
            t('wbs.follow-up-request-s-canceled'),
            canceledRequestId,
          )}
        </AlertBox>
      )}
      <TabsBar tabs={tabs} base="/groups/$groupName/follow-ups" />
      <Outlet />
    </FollowUpsAlertContext.Provider>
  );
}

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups',
)({
  component: FollowUpsLayout,
});
